import { type ReactNode, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { BrandMark } from '@/components/BrandMark'
import { ThemeToggle } from '@/components/ThemeToggle'

type StepShellProps = {
  currentStep: number
  totalSteps: number
  stepKey: string
  direction: number // 1 = forward, -1 = backward
  children: ReactNode
  onBack?: () => void
  onContinue?: () => void
  continueLabel?: string
  continueDisabled?: boolean
  hideControls?: boolean
}

const variants = {
  enter: (direction: number) => ({ x: direction > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({ x: direction > 0 ? -60 : 60, opacity: 0 }),
}

export default function StepShell({
  currentStep,
  totalSteps,
  stepKey,
  direction,
  children,
  onBack,
  onContinue,
  continueLabel = 'Continue',
  continueDisabled = false,
  hideControls = false,
}: StepShellProps) {
  const progress = ((currentStep + 1) / totalSteps) * 100

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || hideControls || continueDisabled || !onContinue) return
      // don't hijack Enter inside multiline fields
      if ((e.target as HTMLElement)?.tagName === 'TEXTAREA') return
      e.preventDefault()
      onContinue()
    },
    [onContinue, hideControls, continueDisabled]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary/5 via-background to-accent/5">
      <div className="h-1 bg-muted">
        <div
          className="h-full bg-primary transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="px-4 sm:px-6 py-4 flex items-center justify-between">
        <BrandMark to="/provider" />
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {currentStep + 1} of {totalSteps}
          </span>
          <ThemeToggle />
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6">
        <div className="w-full max-w-xl">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={stepKey}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: 'easeInOut' }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {!hideControls && (
        <div className="px-4 sm:px-6 py-6 flex items-center justify-between max-w-xl mx-auto w-full">
          {onBack ? (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="size-4" />
              Back
            </Button>
          ) : (
            <div />
          )}
          {onContinue && (
            <Button size="lg" onClick={onContinue} disabled={continueDisabled}>
              {continueLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
